import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { useQuery } from '@tanstack/react-query';

import styles from './ChatCreationPopup.module.scss';

import {
    EhrChatData,
    Optional,
    RecurringSubscriptionAmountCalculationResponse,
} from 'chaika-frontend-types/frontend-cms';
import { Currency } from 'chaika-enums';

import { translate } from '@chaika/i18n/helper';

import { store } from '@store';
import { Money } from '@store/models/Money';
import { recurringSubscriptionTrialDurationQueryOptions } from '@store/RecurringSubscription/queries/recurringSubscriptionTrialDurationQueries';

import { isDefined } from '@utils/isDefined';
import { Declension } from '@utils/Declension';
import { assertIsDefined } from '@utils/assertIsDefined';

import { useEhrSubscription } from '@modules/patient/hooks/useEhrSubscription';

import { RecurringSubscriptionTermsEditor } from '@common/RecurringSubscriptionTermsEditor';

import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';
import { Textarea } from '@components/Textarea';
import { TextAreaType } from '@components/Textarea';
import { Select } from '@components/Select';
import { AsyncUserSelect } from '@components/ModelSelect/AsyncUserSelect';

import { useSubscriptionBillingAmount } from './hooks/useSubscriptionBillingAmount';
import { useRecurringSubscriptionTerms } from './hooks/useRecurringSubscriptionTerms';
import { usePracticeOptions } from './hooks/usePracticeOptions';
import { PopupModifiers } from '@components/Popup';
import { Preloader } from '@components/Preloader';
import { PreloaderModifiers } from '@components/Preloader/Preloader';
import { ChatPreloader } from './ChatPreloader';

interface SubtitleProps {
    doPatientHasChats: boolean;
    isPracticeChat: boolean;
    isSubscriptionTermsLoading: boolean;
    doctorId?: string;
    billingAmount?: RecurringSubscriptionAmountCalculationResponse;
    trialDurationInDays?: number;
}

const Subtitle = observer(
    ({
        doPatientHasChats,
        isPracticeChat,
        isSubscriptionTermsLoading,
        doctorId,
        billingAmount,
        trialDurationInDays,
    }: SubtitleProps): React.ReactElement<any> => {
        return (
            <>
                {!doPatientHasChats && (
                    <div className={classnames([styles.text, 'font_body16Black'])}>
                        {translate('patientDontUseChaikaChatText')}
                    </div>
                )}
                {!isPracticeChat &&
                    (isSubscriptionTermsLoading ? (
                        <div className={styles.preloader}>
                            <Preloader modifiers={[PreloaderModifiers.Small]} />
                        </div>
                    ) : (
                        <>
                            <div className={classnames([styles.text, 'font_body16Black'])}>
                                {translate('$freeChatSubscriptionsText', {
                                    $cost: billingAmount
                                        ? Money.fromKopecks(billingAmount.amount, billingAmount.currency).toString()
                                        : translate('noSubscriptionCost').toLowerCase(),
                                    $days: isDefined(trialDurationInDays)
                                        ? `${trialDurationInDays} ${Declension.days(trialDurationInDays)}`
                                        : translate('limitedTime'),
                                })}
                            </div>
                            {!isDefined(billingAmount) && isDefined(doctorId) && (
                                <div className={styles.text}>
                                    <Button
                                        dataTest={''}
                                        onClick={() => {
                                            window.open(`/user/${doctorId}?currentPage=subscriptionUserPrice`, '_self');
                                        }}
                                        theme={ButtonTheme.Link}
                                        modifiers={[ButtonModifiers.Small]}
                                        label={translate('setSubscriptionCost')}
                                    />
                                </div>
                            )}
                        </>
                    ))}
            </>
        );
    },
);

interface Props {
    patientId: string;
    doctorId?: string;
    fromEntry: boolean;
    doPatientHasChats: boolean;
    isPracticeChat: boolean;
    onChatCreated(): void;
    reloadChats(): Promise<EhrChatData[]>;
}

export const ChatCreationPopup = observer(
    ({
        patientId,
        doctorId,
        fromEntry,
        doPatientHasChats,
        isPracticeChat,
        onChatCreated,
        reloadChats,
    }: Props): React.ReactElement<any> => {
        const { app, authUser, userList } = store;
        const { createPracticeChat, createChatWithDoctor } = useEhrSubscription();

        const [isChatCreated, setChatCreated] = useState(false);

        const [message, setMessage] = useState<Optional<string>>(undefined);
        const [selectedDoctorId, setSelectedDoctorId] = useState<Optional<string>>(undefined);

        const { billingAmount: defaultBillingAmount, isBillingAmountLoading } = useSubscriptionBillingAmount({
            doctorId: doctorId ?? selectedDoctorId,
        });
        const { data: trialDuration, isLoading: isTrialDurationLoading } = useQuery(
            recurringSubscriptionTrialDurationQueryOptions.get(),
        );
        const { selectedPracticeId, practiceOptions, getPracticeName } = usePracticeOptions({
            assistantId: authUser.id,
        });

        const [title, setTitle] = useState<string>(() =>
            doPatientHasChats && !isPracticeChat ? translate('createChat') : translate('sendContactsButtonLabel'),
        );

        const {
            billingAmount,
            billingAmountDuration,
            isUsedSpecialTerms,
            setBillingAmount,
            setBillingAmountDuration,
            toggleIsUsedSpecialTerms,
        } = useRecurringSubscriptionTerms();

        const practiceDoctorsIds = userList.getExisting(authUser.id).practiceDoctorIds ?? [];
        const allowedDoctorIds = [...practiceDoctorsIds, authUser.id];

        const billingAmountData = useMemo(() => {
            if (isNil(billingAmount) || isNil(billingAmountDuration)) {
                return undefined;
            }

            return { billingAmount, billingAmountDuration };
        }, [billingAmount, billingAmountDuration]);

        const isBillingAmountCorrect = isUsedSpecialTerms
            ? isDefined(billingAmount) && isDefined(billingAmountDuration)
            : isDefined(defaultBillingAmount) && defaultBillingAmount.amount !== 0;

        const isDoctorSelected = isPracticeChat || isDefined(doctorId) || isDefined(selectedDoctorId);

        const isCreationDisabled =
            (!doPatientHasChats && isEmpty(message)) ||
            !isDoctorSelected ||
            (!isPracticeChat && !isBillingAmountCorrect) ||
            (isPracticeChat && isEmpty(message)) ||
            (isPracticeChat && isEmpty(selectedPracticeId));

        const onCreateChat = useCallback(async () => {
            await app.doOrShowError({
                action: () => {
                    if (isPracticeChat) {
                        assertIsDefined(
                            selectedPracticeId,
                            'Practice chat is created only after a practice is selected',
                        );
                        return createPracticeChat({
                            patientId,
                            message: message ?? '',
                            practiceId: selectedPracticeId,
                            practiceName: getPracticeName(selectedPracticeId),
                        });
                    } else {
                        const chatDoctorId = doctorId ?? selectedDoctorId;
                        assertIsDefined(chatDoctorId, 'Doctor chat is created only after a doctor is selected');
                        return createChatWithDoctor({
                            doPatientHasChats,
                            fromEntry,
                            patientId,
                            message,
                            billingAmountData,
                            doctorId: chatDoctorId,
                        });
                    }
                },
                errorHandler: error_1 => {
                    app.showPopup({
                        modifiers: [PopupModifiers.Redesign],
                        description: error_1?.message ?? translate('error'),
                    });
                },
            });

            onChatCreated();
            setChatCreated(true);
        }, [
            app,
            doPatientHasChats,
            fromEntry,
            patientId,
            message,
            billingAmountData,
            doctorId,
            selectedDoctorId,
            isPracticeChat,
            selectedPracticeId,
            getPracticeName,
            onChatCreated,
            createPracticeChat,
            createChatWithDoctor,
        ]);

        return (
            <div className={styles.popupRoot}>
                <div className={classnames(['font_h1', styles.title])}>{title}</div>
                <ChatPreloader
                    hideSubscriptionCost={isPracticeChat}
                    startLoader={isChatCreated}
                    doctorId={doctorId ?? selectedDoctorId}
                    practiceId={isPracticeChat ? selectedPracticeId : undefined}
                    updateTitle={setTitle}
                    reloadChats={reloadChats}
                >
                    <Subtitle
                        doPatientHasChats={doPatientHasChats}
                        isPracticeChat={isPracticeChat}
                        isSubscriptionTermsLoading={isBillingAmountLoading || isTrialDurationLoading}
                        doctorId={doctorId ?? selectedDoctorId}
                        billingAmount={defaultBillingAmount}
                        trialDurationInDays={trialDuration?.trialDurationInDays}
                    />
                    {isNil(doctorId) && !isPracticeChat && (
                        <div className={styles.doctorSelect}>
                            <AsyncUserSelect
                                withAnyOption
                                withFullName
                                title={translate('doctor')}
                                dataTest={''}
                                defaultValue={selectedDoctorId}
                                ids={allowedDoctorIds}
                                onlyEpisodeManagers
                                onChange={option => setSelectedDoctorId(option?.value?.toString() ?? undefined)}
                            />
                        </div>
                    )}
                    {isPracticeChat && (
                        <div className={styles.doctorSelect}>
                            <Select
                                disabled
                                dataTest={''}
                                selectedValue={selectedPracticeId ?? null}
                                defaultOptionType="noPracticeWithTgLink"
                                options={practiceOptions}
                                onChange={() => {}}
                            />
                        </div>
                    )}
                    {!isPracticeChat && (
                        <div className={styles.text}>
                            <RecurringSubscriptionTermsEditor
                                isUsedSpecialTerms={isUsedSpecialTerms}
                                currency={defaultBillingAmount?.currency ?? Currency.RUB}
                                billingAmount={billingAmount}
                                billingAmountDuration={billingAmountDuration}
                                onSpecialTermsChange={toggleIsUsedSpecialTerms}
                                onBillingAmountChange={setBillingAmount}
                                onBillingAmountDurationChange={setBillingAmountDuration}
                            />
                        </div>
                    )}
                    {(!doPatientHasChats || isPracticeChat) && (
                        <div className={styles.textarea}>
                            <Textarea
                                dataTest={''}
                                title={translate('textMessage')}
                                value={message ?? ''}
                                type={TextAreaType.TextareaFixed}
                                onChange={event => setMessage(event.target.value)}
                            />
                        </div>
                    )}
                    <div className={styles.actionButtons}>
                        <Button
                            theme={ButtonTheme.Primary}
                            disabled={isCreationDisabled}
                            label={
                                doPatientHasChats && !isPracticeChat
                                    ? translate('createChat')
                                    : translate('sendChatInvite')
                            }
                            onClick={onCreateChat}
                        />
                        <Button
                            theme={ButtonTheme.Secondary}
                            label={translate('cancel')}
                            onClick={app.showPopup.bind(app, null)}
                        />
                    </div>
                </ChatPreloader>
            </div>
        );
    },
);
